<soft-page customClass="!p-0">
  @let detail = mitarbeiterDetail$ | async;
  @let ready = (loading$ | async) === false && !!detail;
  @if (ready && detail?.lstfix; as person) {
    <soft-page-header>
      <div class="mitarbeiter-detail__header" data-testid="statusabfrage-detail-header">
        <soft-ds-avatar size="lg" [initials]="initials(person)" />
        <div class="mitarbeiter-detail__header-text">
          <span class="ds-body-1-semibold">{{ person.vorname }} {{ person.zuname }}</span>
          <span class="ds-caption text-light">Nr. {{ person.persnr }}</span>
        </div>
      </div>
    </soft-page-header>
  } @else if (!ready) {
    <soft-page-header>
      <div class="mitarbeiter-detail__header" aria-busy="true">
        <soft-ds-loading-skeleton [width]="40" [height]="40" radius="50%" />
        <div class="mitarbeiter-detail__header-text">
          <soft-ds-loading-skeleton [width]="180" [height]="16" />
          <soft-ds-loading-skeleton [width]="64" [height]="12" />
        </div>
      </div>
    </soft-page-header>
  }
  <soft-page-panel panelStyle="plain" class="overflow-y-auto">
    @if (ready && detail) {
      <div class="mitarbeiter-detail" data-testid="statusabfrage-detail">
        @if (detail.adresse; as adresse) {
          @if (
            adresse.strasse ||
            adresse.postleitzahl ||
            adresse.ort ||
            adresse.nebenstellefa ||
            adresse.telefonfa ||
            adresse.handyfa ||
            adresse.mail
          ) {
            <section class="mitarbeiter-detail__section">
              <span class="ds-caption-semibold text-light">
                {{
                  '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.KONTAKTDATEN.TITEL' | translate
                }}
              </span>
              <soft-ds-card density="compact" data-testid="statusabfrage-detail-contact">
                <soft-ds-card-body>
                  <div class="mitarbeiter-detail__contact ds-body-2">
                    @if (adresse.strasse || adresse.strassenr) {
                      <span>{{ adresse.strasse }} {{ adresse.strassenr }}</span>
                    }
                    @if (adresse.postleitzahl || adresse.ort) {
                      <span>
                        {{ adresse.landeskennzeichen }}-{{ adresse.postleitzahl }}
                        {{ adresse.ort }}
                      </span>
                    }
                    @if (adresse.nebenstellefa) {
                      <span>Nebenstelle: {{ adresse.nebenstellefa }}</span>
                    }
                    @if (adresse.telefonfa) {
                      <a class="soft-link" [href]="'tel:' + adresse.telefonfa">
                        {{ adresse.telefonfa }}
                      </a>
                    }
                    @if (adresse.handyfa) {
                      <a class="soft-link" [href]="'tel:' + adresse.handyfa">
                        {{ adresse.handyfa }}
                      </a>
                    }
                    @if (adresse.mail) {
                      <a class="soft-link" [href]="'mailto:' + adresse.mail">
                        {{ adresse.mail }}
                      </a>
                    }
                  </div>
                </soft-ds-card-body>
              </soft-ds-card>
            </section>
          }
        }

        @if (detail.dienstplanEintraege?.length) {
          <section class="mitarbeiter-detail__section">
            <span class="ds-caption-semibold text-light">
              {{ '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.DIENSTPLAN.TITEL' | translate }}
            </span>
            <soft-ds-item-group>
              @for (entry of detail.dienstplanEintraege; track $index) {
                <soft-ds-item>
                  <soft-ds-item-leading class="tabular-nums">
                    {{ entry.vonzeit | format: 'TIME.DEFAULT' }} -
                    {{ entry.biszeit | format: 'TIME.DEFAULT' }}
                  </soft-ds-item-leading>
                  <soft-ds-item-title>{{ entry.taetigkeit }}</soft-ds-item-title>
                </soft-ds-item>
              }
            </soft-ds-item-group>
          </section>
        }

        @if (detail.stempel?.length) {
          <section class="mitarbeiter-detail__section">
            <span class="ds-caption-semibold text-light">
              {{ '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.TITEL' | translate }}
            </span>
            <div class="mitarbeiter-detail__stamps">
              @for (stamp of detail.stempel; track $index; let index = $index) {
                @let arrives = stamp.kzkg === 'K';
                @let expanded = isStampExpanded(index);
                <soft-ds-card
                  density="compact"
                  [accent]="arrives ? 'success' : 'warning'"
                  data-testid="statusabfrage-stamp"
                >
                  <soft-ds-card-header
                    class="mitarbeiter-detail__stamp-header"
                    (click)="toggleStamp(index)"
                  >
                    <soft-ds-card-leading
                      variant="tinted"
                      align="center"
                      size="lg"
                      [tone]="arrives ? 'success' : 'warning'"
                    >
                      <soft-ds-icon
                        [name]="
                          arrives
                            ? 'fa-regular fa-person-to-door'
                            : 'fa-regular fa-house-person-leave'
                        "
                      />
                    </soft-ds-card-leading>
                    <soft-ds-card-title>
                      {{
                        (arrives
                          ? '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.KOMMT_HEADER'
                          : '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.GEHT_HEADER'
                        ) | translate
                      }}
                    </soft-ds-card-title>
                    <soft-ds-card-description>
                      {{ stamp.zeit | format: 'TIME.DEFAULT' }}
                    </soft-ds-card-description>
                    <soft-ds-card-trailing align="center">
                      <span (click)="$event.stopPropagation()">
                        <soft-ds-button
                          variant="ghost"
                          size="sm"
                          mode="iconOnly"
                          [attr.aria-expanded]="expanded"
                          [aria-label]="
                            (expanded
                              ? '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.ZUKLAPPEN'
                              : '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.AUFKLAPPEN'
                            ) | translate
                          "
                          [attr.data-testid]="'statusabfrage-stamp-toggle-' + index"
                          (click)="toggleStamp(index)"
                        >
                          <soft-ds-icon
                            [name]="
                              expanded
                                ? 'fa-regular fa-chevron-up'
                                : 'fa-regular fa-chevron-down'
                            "
                          />
                        </soft-ds-button>
                      </span>
                    </soft-ds-card-trailing>
                  </soft-ds-card-header>
                  @if (expanded) {
                    <soft-ds-card-body>
                      <soft-ds-cell-grid
                        [columns]="3"
                        [framed]="false"
                        density="compact"
                        [emptyText]="
                          '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.NICHT_ERFASST'
                            | translate
                        "
                      >
                        <soft-ds-cell
                          [label]="
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.KOMMT_GEHT'
                              | translate
                          "
                          [value]="
                            (arrives
                              ? '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.KOMMT'
                              : '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.GEHT'
                            ) | translate
                          "
                        />
                        <soft-ds-cell
                          [label]="
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.TAG'
                              | translate
                          "
                          [value]="stamp.datum | date: 'EEEE, dd.MM.yyyy'"
                        />
                        <soft-ds-cell
                          [label]="
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.STEMPEL'
                              | translate
                          "
                          [value]="stamp.zeit | format: 'TIME.DEFAULT'"
                        />
                        <soft-ds-cell
                          [label]="
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.CODE'
                              | translate
                          "
                          [value]="stamp.pmscode"
                          format="id"
                        />
                        <soft-ds-cell
                          [label]="
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.UEBERNAHMEDATUM'
                              | translate
                          "
                          [value]="stamp.uebernahmedatum | format: 'DATE_TIME.SECONDS'"
                        />
                        <soft-ds-cell
                          [label]="
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.ANWENDER'
                              | translate
                          "
                          [value]="stamp.anwender"
                        />
                        <soft-ds-cell
                          [label]="
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.GERAET'
                              | translate
                          "
                          [value]="stamp.pmsgeraet"
                        />
                        <soft-ds-cell
                          [label]="
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.ERFASSUNGSDATUM'
                              | translate
                          "
                          [value]="stamp.erfzeit | format: 'DATE_TIME.SECONDS'"
                        />
                        <soft-ds-cell
                          [label]="
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.BEMERKUNG'
                              | translate
                          "
                          [value]="stamp.bemerkung"
                        />
                        <soft-ds-cell
                          [label]="
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.TAETIGKEIT'
                              | translate
                          "
                          [value]="stamp.taetigkeit"
                        />
                        <soft-ds-cell
                          [label]="
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.KST'
                              | translate
                          "
                          [value]="stamp.kst"
                          format="id"
                        />
                        <soft-ds-cell
                          [label]="
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.HERKUNFT'
                              | translate
                          "
                          [value]="stamp.herkunft"
                        />
                        <soft-ds-cell
                          [label]="
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.STEMPEL.GELOESCHT'
                              | translate
                          "
                          [value]="stamp.kzloesch"
                        />
                      </soft-ds-cell-grid>
                    </soft-ds-card-body>
                  }
                </soft-ds-card>
              }
            </div>
          </section>
        }

        @if (termine$ | async; as termine) {
          @if (termine.length > 0) {
            <section class="mitarbeiter-detail__section">
              <span class="ds-caption-semibold text-light">
                {{ '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.TERMINE.TITEL' | translate }}
              </span>
              <soft-ds-item-group>
                @for (termin of termine; track $index) {
                  <soft-ds-item>
                    <soft-ds-item-leading class="tabular-nums">
                      {{ termin.vonzeit | format: 'TIME.DEFAULT' }} -
                      {{ termin.biszeit | format: 'TIME.DEFAULT' }}
                    </soft-ds-item-leading>
                    <soft-ds-item-title>{{ termin.taetigkeit }}</soft-ds-item-title>
                    @if (termin.external) {
                      <soft-ds-item-trailing>
                        <soft-ds-badge>
                          {{
                            '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.TERMINE.EXTERN'
                              | translate
                          }}
                        </soft-ds-badge>
                      </soft-ds-item-trailing>
                    }
                  </soft-ds-item>
                }
              </soft-ds-item-group>
            </section>
          }
        }

        @if (detail.istzeiten?.length) {
          <section class="mitarbeiter-detail__section">
            <span class="ds-caption-semibold text-light">
              {{ '#PMS_STATUSABFRAGE.MITARBEITER_DETAIL.ISTZEIT.TITEL' | translate }}
            </span>
            <soft-ds-item-group>
              @for (istzeit of detail.istzeiten; track $index) {
                <soft-ds-item>
                  <soft-ds-item-leading class="tabular-nums">
                    {{ istzeit.ist_von | format: 'TIME.DEFAULT' }} -
                    {{ istzeit.ist_bis | format: 'TIME.DEFAULT' }}
                  </soft-ds-item-leading>
                  <soft-ds-item-title>{{ istzeit.taetigkeit }}</soft-ds-item-title>
                  <soft-ds-item-trailing class="tabular-nums">
                    {{ istzeit.ist_sekunden | secondsToHours }}
                  </soft-ds-item-trailing>
                </soft-ds-item>
              }
            </soft-ds-item-group>
          </section>
        }
      </div>
    } @else {
      <div
        class="mitarbeiter-detail"
        aria-busy="true"
        data-testid="statusabfrage-detail-skeleton"
      >
        <section class="mitarbeiter-detail__section">
          <soft-ds-loading-skeleton [width]="120" [height]="12" />
          <soft-ds-card density="compact">
            <soft-ds-card-body>
              <div class="mitarbeiter-detail__contact">
                <soft-ds-loading-skeleton [width]="240" [height]="12" />
                <soft-ds-loading-skeleton [width]="160" [height]="12" />
                <soft-ds-loading-skeleton [width]="200" [height]="12" />
              </div>
            </soft-ds-card-body>
          </soft-ds-card>
        </section>
        @for (section of skeletonSections; track section) {
          <section class="mitarbeiter-detail__section">
            <soft-ds-loading-skeleton [width]="120" [height]="12" />
            <soft-ds-loading-skeleton height="2.5rem" />
            <soft-ds-loading-skeleton height="2.5rem" />
          </section>
        }
      </div>
    }
  </soft-page-panel>
</soft-page>
